import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import './CLI.css'

const CLI = () => {
  const [activeSection, setActiveSection] = useState('overview')

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'installation', title: 'Installation' },
    { id: 'authentication', title: 'Authentication' },
    { id: 'deploying', title: 'Deploying' },
    { id: 'project-management', title: 'Project Management' },
    { id: 'environment', title: 'Environment Variables' },
    { id: 'domains', title: 'Custom Domains' },
    { id: 'commands', title: 'Command Reference' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`cli-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  return (
    <div className="cli-page">
      <div className="cli-content-wrapper">
        <div className="cli-main-content">
          <h1>CLI</h1>
          <p className="intro">
            The <strong>Aedify CLI</strong> lets you deploy and manage projects from the terminal.
            Deploy local directories or GitHub repos, manage environment variables, custom domains,
            and monitor builds — all without leaving the command line.
          </p>

          <section id="cli-overview" className="cli-section">
            <h2>Overview</h2>
            <p>
              The Aedify CLI provides a Vercel-style workflow for deploying applications.
              Each local directory is linked to a persistent Aedify project, and every deploy
              creates a new deployment under that same project.
            </p>
            <ul>
              <li><strong>Local Deploy:</strong> Deploy directly from your local directory — no git push required</li>
              <li><strong>GitHub Deploy:</strong> Deploy from any public or private GitHub repository</li>
              <li><strong>Project Linking:</strong> Bind a local directory to an Aedify project with <code>aedify link</code></li>
              <li><strong>Auto-Detection:</strong> Framework detection (Vite, Next.js, Nuxt, etc.) is fully automatic</li>
              <li><strong>Full Management:</strong> Environment variables, custom domains, build logs, scaling, and rollbacks</li>
            </ul>
          </section>

          <section id="cli-installation" className="cli-section">
            <h2>Installation</h2>
            <p>
              Requires <strong>Node.js 18+</strong>. Install globally via npm:
            </p>
            <pre><code>npm install -g aedify.ai</code></pre>
            <p>
              Or run without installing using <code>npx</code>:
            </p>
            <pre><code>npx aedify.ai deploy --local</code></pre>
            <p>
              Verify the installation:
            </p>
            <pre><code>aedify --version</code></pre>
          </section>

          <section id="cli-authentication" className="cli-section">
            <h2>Authentication</h2>
            <p>
              Before using the CLI, authenticate with your Aedify API key. If you don't have one yet,
              create it from the <a href="https://aedify.ai/console/api-keys" target="_blank" rel="noopener noreferrer">Aedify Console</a>.
              See the <Link to="/resources/api-keys">API Keys documentation</Link> for details.
            </p>

            <h3>Login</h3>
            <pre><code>{`# Interactive (prompts for key)
aedify login

# Non-interactive
aedify login --key YOUR_API_KEY`}</code></pre>
            <p>
              Your API key is stored locally at <code>~/.aedify/credentials.json</code>.
            </p>

            <h3>Check Auth Status</h3>
            <pre><code>aedify whoami</code></pre>

            <h3>Logout</h3>
            <pre><code>aedify logout</code></pre>
          </section>

          <section id="cli-deploying" className="cli-section">
            <h2>Deploying</h2>
            <p>
              The CLI supports two deployment methods: deploying from a local directory and deploying from a GitHub repository.
            </p>

            <h3>Deploy from Local Directory</h3>
            <p>
              The most common workflow. Deploy your current working directory:
            </p>
            <pre><code>{`# First deploy — creates a new project
aedify deploy --local --name my-app

# Subsequent deploys — reuses the linked project
aedify deploy --local`}</code></pre>
            <p>
              On the first deploy, the CLI creates a project and saves the link in <code>.aedify/project.json</code>.
              Every subsequent <code>deploy --local</code> from the same directory deploys to the same project — no new project is created.
            </p>

            <h3>Deploy a Specific Directory</h3>
            <pre><code>{`# Deploy a subdirectory
aedify deploy --dir ./frontend --name my-frontend

# If already linked, just:
aedify deploy --dir ./frontend`}</code></pre>

            <h3>Deploy from GitHub</h3>
            <pre><code>{`aedify deploy --repo https://github.com/user/repo --branch main --name my-app`}</code></pre>

            <h3>Deploy with Environment Variables</h3>
            <pre><code>aedify deploy --local --env API_KEY=xxx DATABASE_URL=postgres://...</code></pre>

            <h3>Deploy with Resource Limits</h3>
            <pre><code>aedify deploy --local --cpu 1 --memory 1024</code></pre>

            <h3>How Linking Works</h3>
            <p>
              When you first deploy a directory, the CLI saves a <code>.aedify/project.json</code> file
              that binds the directory to the Aedify project. This is the same model as Vercel's <code>.vercel/project.json</code>.
            </p>
            <pre><code>{`# Manually link to an existing project
aedify link

# Link with a specific slug
aedify link --slug my-app-abc123

# Remove the link
aedify unlink`}</code></pre>
            <p>
              Add <code>.aedify/</code> to your <code>.gitignore</code>.
            </p>
          </section>

          <section id="cli-project-management" className="cli-section">
            <h2>Project Management</h2>

            <h3>List Projects</h3>
            <pre><code>aedify projects list</code></pre>

            <h3>Project Details</h3>
            <pre><code>{`# Uses linked project
aedify projects info

# Or specify a slug
aedify projects info my-app-abc123`}</code></pre>

            <h3>Project Status</h3>
            <pre><code>aedify status</code></pre>

            <h3>Redeploy</h3>
            <pre><code>aedify redeploy</code></pre>

            <h3>Rollback</h3>
            <p>Rolls back to the previous successful build:</p>
            <pre><code>aedify rollback</code></pre>

            <h3>Build History</h3>
            <pre><code>{`aedify builds list
aedify builds logs <buildId>`}</code></pre>

            <h3>Runtime Logs</h3>
            <pre><code>{`aedify logs
aedify logs -f  # follow/stream`}</code></pre>

            <h3>Open in Browser</h3>
            <pre><code>aedify open</code></pre>

            <h3>Scaling</h3>
            <pre><code>{`# View current resources
aedify scale

# Update
aedify scale --cpu 2 --memory 2048`}</code></pre>

            <h3>Delete a Project</h3>
            <pre><code>{`aedify projects delete my-app-abc123
aedify projects delete my-app-abc123 -y  # skip confirmation`}</code></pre>
          </section>

          <section id="cli-environment" className="cli-section">
            <h2>Environment Variables</h2>

            <h3>List</h3>
            <pre><code>{`aedify env list
aedify env list --show  # reveal values`}</code></pre>

            <h3>Set</h3>
            <pre><code>aedify env set KEY=value ANOTHER=value</code></pre>

            <h3>Remove</h3>
            <pre><code>aedify env unset KEY</code></pre>

            <h3>Pull to Local .env</h3>
            <pre><code>{`aedify env pull
aedify env pull -f .env.local  # custom file`}</code></pre>
          </section>

          <section id="cli-domains" className="cli-section">
            <h2>Custom Domains</h2>

            <h3>List Domains</h3>
            <pre><code>aedify domains list</code></pre>

            <h3>Add a Domain</h3>
            <pre><code>aedify domains add example.com</code></pre>

            <h3>Remove a Domain</h3>
            <pre><code>aedify domains remove example.com</code></pre>
          </section>

          <section id="cli-commands" className="cli-section">
            <h2>Command Reference</h2>

            <h3>Authentication</h3>
            <div className="cli-command-item">
              <h4>aedify login</h4>
              <p>Authenticate with your Aedify API key.</p>
              <pre><code>aedify login [--key &lt;key&gt;] [--url &lt;api-url&gt;]</code></pre>
            </div>
            <div className="cli-command-item">
              <h4>aedify logout</h4>
              <p>Remove stored credentials.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify whoami</h4>
              <p>Show the current authenticated user.</p>
              <pre><code>aedify whoami [--json]</code></pre>
            </div>

            <h3>Deployment</h3>
            <div className="cli-command-item">
              <h4>aedify deploy</h4>
              <p>Deploy a project from a local directory or GitHub.</p>
              <pre><code>{`aedify deploy [options]

Options:
  --local            Deploy from local directory
  --dir <path>       Directory to deploy (default: .)
  --repo <url>       GitHub repo URL
  --branch <branch>  Branch (default: main)
  --name <name>      Project name
  --cpu <value>      CPU cores (e.g. 0.5, 1, 2)
  --memory <value>   Memory in MB (e.g. 512, 1024)
  --env <pairs...>   Environment variables (KEY=VALUE)`}</code></pre>
            </div>
            <div className="cli-command-item">
              <h4>aedify redeploy [slug]</h4>
              <p>Redeploy the current version. Uses linked project if no slug is provided.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify rollback [slug]</h4>
              <p>Rollback to the previous successful build.</p>
            </div>

            <h3>Project Management</h3>
            <div className="cli-command-item">
              <h4>aedify link [--slug &lt;slug&gt;]</h4>
              <p>Link the current directory to an Aedify project.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify unlink</h4>
              <p>Remove the project link from the current directory.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify projects list [--json]</h4>
              <p>List all projects.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify projects info [slug] [--json]</h4>
              <p>Show project details.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify projects delete [slug] [-y]</h4>
              <p>Delete a project. Use <code>-y</code> to skip confirmation.</p>
            </div>

            <h3>Monitoring</h3>
            <div className="cli-command-item">
              <h4>aedify status [slug] [--json]</h4>
              <p>Show project deployment status.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify builds list [slug] [--json]</h4>
              <p>List build history.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify builds logs &lt;buildId&gt; [--json]</h4>
              <p>View build logs.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify logs [slug] [-f] [--json]</h4>
              <p>View runtime logs. Use <code>-f</code> to stream in real-time.</p>
            </div>

            <h3>Configuration</h3>
            <div className="cli-command-item">
              <h4>aedify env list [slug] [--show] [--json]</h4>
              <p>List environment variables. Use <code>--show</code> to reveal values.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify env set [slug] [pairs...]</h4>
              <p>Set environment variables. Format: <code>KEY=VALUE</code>.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify env unset [slug] [keys...]</h4>
              <p>Remove environment variables.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify env pull [slug] [-f &lt;path&gt;] [-y]</h4>
              <p>Download environment variables to a local <code>.env</code> file.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify domains list [slug] [--json]</h4>
              <p>List domains for a project.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify domains add [slug] &lt;domain&gt;</h4>
              <p>Add a custom domain.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify domains remove [slug] &lt;domain&gt;</h4>
              <p>Remove a custom domain.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify scale [slug] [--cpu &lt;value&gt;] [--memory &lt;value&gt;]</h4>
              <p>View or update CPU/memory resources.</p>
            </div>
            <div className="cli-command-item">
              <h4>aedify open [slug]</h4>
              <p>Open the project URL in your browser.</p>
            </div>
          </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="cli-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#cli-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`cli-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Quick Tips</h3>
            <ul className="tips-list">
              <li>Use <code>npx aedify.ai</code> to run without installing</li>
              <li>First deploy auto-creates a project link</li>
              <li>Add <code>.aedify/</code> to <code>.gitignore</code></li>
              <li>All commands accept <code>--json</code> for scripting</li>
              <li>Use <code>aedify env pull</code> to sync env vars locally</li>
              <li>Rollback reverts to the previous successful build</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default CLI
